import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Icon from '../components/common/Icon';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorBanner from '../components/common/ErrorBanner';
import { formatRelativeTime } from '../utils/formatters';
import { userAPI } from '../services/api';
import authService from '../services/authService';
import './AdminUsers.css';

const AdminUsers = () => {
    const isSuperAdmin = authService.isSuperAdmin();
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [agencyFilter, setAgencyFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [formData, setFormData] = useState({
        email: '',
        firstName: '',
        lastName: '',
        role: 'Basic',
        agency: 'JVWEB',
        temporaryPassword: ''
    });
    const [editFormData, setEditFormData] = useState({
        email: '',
        firstName: '',
        lastName: '',
        role: 'Basic',
        isActive: true,
        resetPassword: false
    });
    const [creating, setCreating] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [createSuccess, setCreateSuccess] = useState(null);
    const [updateSuccess, setUpdateSuccess] = useState(null);
    const [passwordResetSuccess, setPasswordResetSuccess] = useState(null); // Separate modal for password reset
    const [copiedPassword, setCopiedPassword] = useState(false);

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        if (agencyFilter === 'all') {
            setFilteredUsers(users);
        } else {
            setFilteredUsers(users.filter(user => user.agency && user.agency === agencyFilter));
        }
    }, [users, agencyFilter]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await userAPI.list();
            const users = Array.isArray(response) ? response : [];
            const activeUsers = users.filter(user => user.isActive);
            setUsers(activeUsers);
            setError(null);
        } catch (err) {
            setError(err.message || 'Error loading users');
        } finally {
            setLoading(false);
        }
    };

    const getUniqueAgencies = () => {
        const agencies = [...new Set(users.map(user => user.agency))];
        return agencies.sort();
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setCreating(true);
        setCreateSuccess(null);
        setError(null);

        try {
            const response = await userAPI.create(formData);
            
            setCreateSuccess({
                email: response.email,
                password: response.temporaryPassword,
                firstName: response.firstName,
                lastName: response.lastName
            });

            // Reset form
            setFormData({
                email: '',
                firstName: '',
                lastName: '',
                role: 'Basic',
                agency: 'JVWEB',
                temporaryPassword: ''
            });

            // Refresh user list
            await fetchUsers();
            
            // DON'T auto-close - user must close manually after copying
        } catch (err) {
            setError(err.message || 'Error creating user');
        } finally {
            setCreating(false);
        }
    };

    const generatePassword = () => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
        let password = '';
        for (let i = 0; i < 12; i++) {
            password += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setFormData(prev => ({ ...prev, temporaryPassword: password }));
    };

    const copyPasswordToClipboard = async () => {
        // Try to copy from createSuccess, passwordResetSuccess, or updateSuccess
        const passwordToCopy = createSuccess?.password || passwordResetSuccess?.password || updateSuccess?.password;
        
        if (passwordToCopy) {
            try {
                await navigator.clipboard.writeText(passwordToCopy);
                setCopiedPassword(true);
                setTimeout(() => setCopiedPassword(false), 2000);
            } catch (err) {
                console.error('Failed to copy password:', err);
                // Fallback: create a temporary input to copy
                const textArea = document.createElement('textarea');
                textArea.value = passwordToCopy;
                textArea.style.position = 'fixed';
                textArea.style.opacity = '0';
                document.body.appendChild(textArea);
                textArea.select();
                try {
                    document.execCommand('copy');
                    setCopiedPassword(true);
                    setTimeout(() => setCopiedPassword(false), 2000);
                } catch (fallbackErr) {
                    console.error('Fallback copy failed:', fallbackErr);
                    alert('Impossible de copier le mot de passe. Veuillez le copier manuellement.');
                }
                document.body.removeChild(textArea);
            }
        }
    };

    const closeSuccessBanner = () => {
        setCreateSuccess(null);
        setCopiedPassword(false);
    };

    const closePasswordResetModal = () => {
        setUpdateSuccess(null);
        setCopiedPassword(false);
    };

    const openEditModal = (user) => {
        setEditingUser(user);
        setEditFormData({
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            role: user.role,
            isActive: user.isActive,
            resetPassword: false
        });
        setShowEditModal(true);
        setUpdateSuccess(null);
        setError(null);
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setEditingUser(null);
        setUpdateSuccess(null);
        setCopiedPassword(false);
        setError(null);
        // Reset the form data to clear the resetPassword checkbox
        setEditFormData({
            email: '',
            firstName: '',
            lastName: '',
            role: 'Basic',
            isActive: true,
            resetPassword: false
        });
    };

    const handleEditFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        setUpdating(true);
        setError(null);

        // Debug: log what we're sending
        console.log('Updating user with data:', editFormData);

        try {
            const result = await userAPI.update(editingUser.userId, editFormData);
            
            // Refresh user list
            await fetchUsers();
            
            // If password was reset, show in separate modal and close edit modal
            if (result.newPassword) {
                setPasswordResetSuccess({
                    firstName: result.firstName,
                    lastName: result.lastName,
                    email: result.email,
                    password: result.newPassword
                });
                closeEditModal(); // Close the edit modal immediately
            } else {
                // Just show simple success message
                setUpdateSuccess({
                    firstName: result.firstName,
                    lastName: result.lastName,
                    email: result.email,
                    password: null
                });
                // Close modal after delay
                setTimeout(() => {
                    closeEditModal();
                }, 2000);
            }
        } catch (err) {
            setError(err.message || 'Erreur lors de la modification');
        } finally {
            setUpdating(false);
        }
    };

    const openDeleteConfirmation = (user) => {
        setUserToDelete(user);
        setError(null);
    };

    const closeDeleteConfirmation = () => {
        setUserToDelete(null);
        setError(null);
    };

    const handleDeleteUser = async () => {
        if (!userToDelete) return;

        setDeleting(true);
        setError(null);

        try {
            await userAPI.delete(userToDelete.userId);
            
            // Refresh user list
            await fetchUsers();
            
            // Close confirmation
            closeDeleteConfirmation();
        } catch (err) {
            setError(err.message || 'Erreur lors de la suppression');
        } finally {
            setDeleting(false);
        }
    };

    if (loading && users.length === 0) {
        return (
            <Layout>
                <div className="admin-users">
                    <LoadingSpinner />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="admin-users">
                {isSuperAdmin && (
                    <div className="superadmin-banner">
                        <div className="superadmin-badge">
                            <Icon name="Shield" size={18} />
                            <span>SuperAdmin</span>
                        </div>
                        <div className="superadmin-info">
                            Vous gérez les utilisateurs de toutes les agences
                        </div>
                    </div>
                )}

                <div className="admin-header">
                    <div>
                        <h1>Gestion des utilisateurs</h1>
                        <p className="admin-subtitle">Créer et gérer les comptes utilisateurs</p>
                    </div>
                    <Button
                        variant="primary"
                        onClick={() => setShowCreateForm(!showCreateForm)}
                        icon={<Icon name={showCreateForm ? "X" : "UserPlus"} size={20} />}
                    >
                        {showCreateForm ? 'Annuler' : 'Nouvel utilisateur'}
                    </Button>
                </div>

                {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

                {showCreateForm && (
                    <Card className="create-user-card">
                        <h3>Créer un nouvel utilisateur</h3>
                        
                        {createSuccess && (
                            <div className="success-banner">
                                <Icon name="CheckCircle" size={24} />
                                <div className="success-content">
                                    <strong>✅ Utilisateur créé avec succès !</strong>
                                    <p><strong>{createSuccess.firstName} {createSuccess.lastName}</strong></p>
                                    <p>Email: <code>{createSuccess.email}</code></p>
                                    <div className="password-display">
                                        <span>Mot de passe: </span>
                                        <code className="password-code">{createSuccess.password}</code>
                                        <button
                                            type="button"
                                            className="copy-password-btn"
                                            onClick={copyPasswordToClipboard}
                                            title="Copier le mot de passe"
                                        >
                                            <Icon name={copiedPassword ? "Check" : "Copy"} size={16} />
                                            {copiedPassword ? 'Copié !' : 'Copier'}
                                        </button>
                                    </div>
                                    <small>⚠️ Notez ce mot de passe, il ne sera plus affiché après fermeture</small>
                                </div>
                                <button
                                    type="button"
                                    className="close-success-btn"
                                    onClick={closeSuccessBanner}
                                    title="Fermer"
                                >
                                    <Icon name="X" size={20} />
                                </button>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="create-user-form">
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="firstName">Prénom *</label>
                                    <input
                                        type="text"
                                        id="firstName"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleInputChange}
                                        required
                                        disabled={creating}
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="lastName">Nom *</label>
                                    <input
                                        type="text"
                                        id="lastName"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleInputChange}
                                        required
                                        disabled={creating}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="email">Email *</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                    disabled={creating}
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="role">Rôle</label>
                                    <select
                                        id="role"
                                        name="role"
                                        value={formData.role}
                                        onChange={handleInputChange}
                                        disabled={creating}
                                    >
                                        <option value="Basic">Utilisateur</option>
                                        <option value="Admin">Administrateur</option>
                                        {isSuperAdmin && <option value="SuperAdmin">⭐ SuperAdmin</option>}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="temporaryPassword">
                                        Mot de passe temporaire
                                        <button
                                            type="button"
                                            className="generate-password-btn"
                                            onClick={generatePassword}
                                            disabled={creating}
                                        >
                                            <Icon name="RefreshCw" size={14} />
                                            Générer
                                        </button>
                                    </label>
                                    <input
                                        type="text"
                                        id="temporaryPassword"
                                        name="temporaryPassword"
                                        value={formData.temporaryPassword}
                                        onChange={handleInputChange}
                                        placeholder="Laissez vide pour 'ChangeMe123!'"
                                        disabled={creating}
                                    />
                                </div>
                            </div>

                            {isSuperAdmin && (
                                <div className="form-group">
                                    <label htmlFor="agency">Agence *</label>
                                    <select
                                        id="agency"
                                        name="agency"
                                        value={formData.agency}
                                        onChange={handleInputChange}
                                        disabled={creating}
                                    >
                                        <option value="JVWEB">JVWEB</option>
                                        <option value="ADSUP">ADSUP</option>
                                    </select>
                                </div>
                            )}

                            <div className="form-actions">
                                <Button
                                    type="submit"
                                    variant="primary"
                                    disabled={creating}
                                    icon={creating ? <LoadingSpinner size="small" /> : <Icon name="UserPlus" size={18} />}
                                >
                                    {creating ? 'Création...' : 'Créer l\'utilisateur'}
                                </Button>
                            </div>
                        </form>
                    </Card>
                )}

                <Card className="users-list-card">
                    <div className="users-list-header">
                        <h3>Utilisateurs ({filteredUsers.length})</h3>
                        {isSuperAdmin && users.length > 0 && (
                            <div className="agency-filter">
                                <label htmlFor="agency-filter">Agence:</label>
                                <select 
                                    id="agency-filter"
                                    value={agencyFilter} 
                                    onChange={(e) => setAgencyFilter(e.target.value)}
                                >
                                    <option value="all">Toutes les agences</option>
                                    {getUniqueAgencies().map(agency => (
                                        <option key={agency} value={agency}>{agency}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                    
                    <div className="table-container">
                        <table className="users-table">
                            <thead>
                                <tr>
                                    <th>Nom</th>
                                    <th>Email</th>
                                    {isSuperAdmin && <th>Agence</th>}
                                    <th>Rôle</th>
                                    <th>Statut</th>
                                    <th>Créé le</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map(user => (
                                    <tr key={user.userId}>
                                        <td className="user-name">
                                            {user.firstName} {user.lastName}
                                        </td>
                                        <td className="user-email">{user.email}</td>
                                        {isSuperAdmin && (
                                            <td className="user-agency">
                                                <span className="agency-badge">{user.agency}</span>
                                            </td>
                                        )}
                                        <td>
                                            <span className={`role-badge ${user.role.toLowerCase()}`}>
                                                {user.role === 'SuperAdmin' ? '⭐ SuperAdmin' : user.role === 'Admin' ? '👑 Administrateur' : '👤 Utilisateur'}
                                            </span>
                                        </td>
                                        <td className="user-status">
                                            <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                                                {user.isActive ? 'Actif' : 'Inactif'}
                                            </span>
                                        </td>
                                        <td className="user-created">
                                            {formatRelativeTime(user.createdAt)}
                                        </td>
                                        <td className="user-actions">
                                            <button 
                                                className="btn-action btn-edit"
                                                onClick={() => openEditModal(user)}
                                                title={user.role === 'SuperAdmin' && !isSuperAdmin ? 'Seul un SuperAdmin peut modifier un SuperAdmin' : 'Modifier l\'utilisateur'}
                                                disabled={user.role === 'SuperAdmin' && !isSuperAdmin}
                                            >
                                                <Icon name="Edit" size={16} />
                                            </button>
                                            <button 
                                                className="btn-action btn-delete"
                                                onClick={() => openDeleteConfirmation(user)}
                                                title={user.role === 'SuperAdmin' && !isSuperAdmin ? 'Seul un SuperAdmin peut supprimer un SuperAdmin' : 'Supprimer l\'utilisateur'}
                                                disabled={user.role === 'SuperAdmin' && !isSuperAdmin}
                                            >
                                                <Icon name="Trash2" size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>

                {/* Edit User Modal */}
                {showEditModal && (
                    <div className="modal-overlay" onClick={closeEditModal}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Modifier l'utilisateur</h2>
                                <button className="close-modal-btn" onClick={closeEditModal}>
                                    <Icon name="X" size={24} />
                                </button>
                            </div>

                            <div className="modal-body">
                                {updateSuccess && !updateSuccess.password && (
                                    <div className="success-banner-simple">
                                        <Icon name="CheckCircle" size={20} />
                                        <span>Utilisateur modifié avec succès !</span>
                                    </div>
                                )}

                                {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

                                <form onSubmit={handleUpdateSubmit} className="edit-user-form">
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="edit-firstName">Prénom *</label>
                                            <input
                                                type="text"
                                                id="edit-firstName"
                                                name="firstName"
                                                value={editFormData.firstName}
                                                onChange={handleEditFormChange}
                                                required
                                                disabled={updating}
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="edit-lastName">Nom *</label>
                                            <input
                                                type="text"
                                                id="edit-lastName"
                                                name="lastName"
                                                value={editFormData.lastName}
                                                onChange={handleEditFormChange}
                                                required
                                                disabled={updating}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-email">Email *</label>
                                        <input
                                            type="email"
                                            id="edit-email"
                                            name="email"
                                            value={editFormData.email}
                                            onChange={handleEditFormChange}
                                            required
                                            disabled={updating}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-role">Rôle *</label>
                                        <select
                                            id="edit-role"
                                            name="role"
                                            value={editFormData.role}
                                            onChange={handleEditFormChange}
                                            disabled={updating}
                                        >
                                            <option value="Basic">Utilisateur</option>
                                            <option value="Admin">Administrateur</option>
                                            {isSuperAdmin && <option value="SuperAdmin">⭐ SuperAdmin</option>}
                                        </select>
                                    </div>

                                    <div className="form-group checkbox-group">
                                        <label>
                                            <input
                                                type="checkbox"
                                                name="isActive"
                                                checked={editFormData.isActive}
                                                onChange={handleEditFormChange}
                                                disabled={updating}
                                            />
                                            <span>Compte actif</span>
                                        </label>
                                    </div>

                                    <div className="form-group checkbox-group reset-password-group">
                                        <label>
                                            <input
                                                type="checkbox"
                                                name="resetPassword"
                                                checked={editFormData.resetPassword}
                                                onChange={handleEditFormChange}
                                                disabled={updating}
                                            />
                                            <span>Réinitialiser le mot de passe</span>
                                        </label>
                                        {editFormData.resetPassword && (
                                            <small className="warning-text">
                                                <Icon name="AlertTriangle" size={14} />
                                                Un nouveau mot de passe aléatoire sera généré
                                            </small>
                                        )}
                                    </div>

                                    <div className="form-actions">
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            onClick={closeEditModal}
                                            disabled={updating}
                                        >
                                            Annuler
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            disabled={updating}
                                        >
                                            {updating ? 'Modification...' : 'Enregistrer'}
                                        </Button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {userToDelete && (
                    <div className="modal-overlay" onClick={closeDeleteConfirmation}>
                        <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Confirmer la suppression</h2>
                                <button className="close-modal-btn" onClick={closeDeleteConfirmation}>
                                    <Icon name="X" size={24} />
                                </button>
                            </div>

                            <div className="modal-body">
                                {error && <ErrorBanner message={error} onClose={() => setError(null)} />}
                                
                                <div className="delete-confirmation">
                                    <div className="warning-icon">
                                        <Icon name="AlertTriangle" size={48} />
                                    </div>
                                    <p className="delete-message">
                                        Êtes-vous sûr de vouloir supprimer l'utilisateur <strong>{userToDelete.firstName} {userToDelete.lastName}</strong> ?
                                    </p>
                                    <p className="delete-email">
                                        <code>{userToDelete.email}</code>
                                    </p>
                                    <p className="delete-warning">
                                        ⚠️ Cette action désactivera le compte de manière permanente.
                                    </p>
                                </div>

                                <div className="form-actions">
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={closeDeleteConfirmation}
                                        disabled={deleting}
                                    >
                                        Annuler
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="danger"
                                        onClick={handleDeleteUser}
                                        disabled={deleting}
                                        icon={<Icon name="Trash2" size={18} />}
                                    >
                                        {deleting ? 'Suppression...' : 'Supprimer'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Password Reset Success Modal */}
                {passwordResetSuccess && (
                    <div className="modal-overlay" onClick={closePasswordResetModal}>
                        <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>✅ Mot de passe réinitialisé</h2>
                                <button className="close-modal-btn" onClick={closePasswordResetModal}>
                                    <Icon name="X" size={24} />
                                </button>
                            </div>

                            <div className="modal-body">
                                <div className="password-reset-success">
                                    <div className="success-icon">
                                        <Icon name="CheckCircle" size={64} />
                                    </div>
                                    
                                    <p className="success-user-info">
                                        Nouveau mot de passe pour <strong>{passwordResetSuccess.firstName} {passwordResetSuccess.lastName}</strong>
                                    </p>
                                    
                                    <p className="success-email">
                                        Email: <code>{passwordResetSuccess.email}</code>
                                    </p>

                                    <div className="password-display-large">
                                        <label>Mot de passe temporaire :</label>
                                        <code className="password-code-large">{passwordResetSuccess.password}</code>
                                        <button
                                            type="button"
                                            className="copy-password-btn"
                                            onClick={copyPasswordToClipboard}
                                            title="Copier le mot de passe"
                                        >
                                            <Icon name={copiedPassword ? "Check" : "Copy"} size={16} />
                                            {copiedPassword ? 'Copié !' : 'Copier'}
                                        </button>
                                    </div>

                                    <div className="password-warning">
                                        <Icon name="AlertTriangle" size={20} />
                                        <p>⚠️ <strong>Important :</strong> Notez ce mot de passe, il ne sera plus affiché après fermeture de cette fenêtre.</p>
                                    </div>
                                </div>

                                <div className="form-actions">
                                    <Button
                                        type="button"
                                        variant="primary"
                                        onClick={closePasswordResetModal}
                                    >
                                        J'ai noté le mot de passe
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default AdminUsers;
